import { css } from '@emotion/react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import useSavingRegistry, { SavingRegistryContext } from 'shared/hooks/use-saving-registry';
import { useSelector } from 'react-redux';
import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { RootState } from 'redux/schemas';
import { ComponentType, LectureComponent } from 'redux/schemas/models/lecture-component';
import { FlipCard } from 'redux/schemas/models/flip-card';
import { updateFlipCardDeckLectureComponent, FlipCardUpsertAttributes } from 'redux/actions/flip-cards';
import { resetNewComponent } from 'redux/actions/lecture-components';
import { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import BaseLectureComponentContext from 'lecture_pages/directives/components/base-lecture-component/context';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';

import FlipCardGrid from './flip-card-grid';
import FlipCardEditModal from './flip-card-edit-modal';
import { FlipCardData } from './flip-card';
import { LectureComponentProps, LecturePageMode } from '..';

type PendingSaveAction = { abort: () => void };

const toFlipCardData = (flipCards: FlipCard[] | undefined): FlipCardData[] => (
  [...(flipCards ?? [])]
    .sort((a, b) => a.index - b.index)
    .map((card) => ({ id: card.id, front: card.frontBody, back: card.backBody }))
);

const FlipCardLectureComponent = (props: LectureComponentProps<ComponentType.FLIP_CARD>) => {
  const { lectureComponent, mode } = props;
  const dispatch = useAppDispatch();
  const params = useLecturePageParams();
  const { sharedProps, setSharedProps } = useContext(BaseLectureComponentContext);
  const [showEditModal, setShowEditModal] = useState(false);

  const lecturePageId = useSelector((state: RootState) => state.app.lecturePage.currentLectureId);
  const lecturePage = useSelector((state: RootState) => state.models.lecturePages[lecturePageId]);
  const isNewComponent = useSelector((state: RootState) => state.app.newComponent === lectureComponent?.id) ?? false;

  const savingRegistry = useSavingRegistry();
  const { isSaving } = savingRegistry;

  const deck = lectureComponent.flipCardDeck;

  const initialCards: FlipCardData[] = useMemo(() => toFlipCardData(deck?.flipCards), [deck]);

  const openEditModal = () => {
    setShowEditModal(true);
  };

  useEffect(() => {
    if (isNewComponent && mode === LecturePageMode.EDIT) {
      openEditModal();
      dispatch(resetNewComponent());
    }
  }, [isNewComponent, mode, dispatch]);

  // Only the most recent save matters, so abort whatever is still in flight.
  const pendingSaveActionRef = useRef<PendingSaveAction | null>(null);

  const saveCards = (draftCards: FlipCardData[]) => {
    pendingSaveActionRef.current?.abort();

    const cards: FlipCardUpsertAttributes[] = draftCards.map((card, index) => ({
      ...(card.id > 0 ? { id: card.id } : {}),
      frontBody: card.front,
      backBody: card.back,
      index,
    }));

    const action = dispatch(updateFlipCardDeckLectureComponent({
      catalogId: params.catalogId,
      lecturePageId: props.currentLecture.id,
      lectureComponentId: lectureComponent.id,
      index: lectureComponent.index,
      flipCardDeckId: deck?.id,
      cards,
    }));
    pendingSaveActionRef.current = action;

    return action.unwrap().then((updatedComponent: LectureComponent<ComponentType.FLIP_CARD>) => (
      toFlipCardData(updatedComponent.flipCardDeck?.flipCards)
    ));
  };

  useEffect(() => {
    const extraOptions: NvDropdownOption[] = [];
    if (!lecturePage?.isLinked) {
      extraOptions.push({
        type: 'text',
        text: t.LECTURE_PAGES.COMPONENTS.DROPDOWN.EDIT_COMPONENT(),
        callback: () => openEditModal(),
      });
    }

    setSharedProps({
      ...sharedProps,
      extraOptions: {
        mode: 'prepend',
        options: extraOptions,
      },
    });
  }, [setSharedProps, lecturePage]);

  const editModalStyles = css`
    .bs4-modal-content {
      max-height: 90vh;
    }
  `;

  return (
    <div className='py-4 mt-2 mb-2'>
      <FlipCardGrid cards={initialCards} />
      {showEditModal && (
        <SavingRegistryContext.Provider value={savingRegistry}>
          <NvModal
            type={ModalType.DYNAMIC}
            header={t.LECTURE_PAGES.COMPONENTS.DROPDOWN.EDIT_COMPONENT()}
            body={(
              <FlipCardEditModal
                cards={initialCards}
                onSave={saveCards}
                closeModal={() => setShowEditModal(false)}
              />
            )}
            show={showEditModal}
            onClose={() => { if (!isSaving()) setShowEditModal(false); }}
            width={800}
            fullHeight={false}
            modalStyles={editModalStyles}
          />
        </SavingRegistryContext.Provider>
      )}
    </div>
  );
};

export default FlipCardLectureComponent;
